import { useEffect, useState } from 'react';

/** Opção normalizada consumida pelos autocompletes de lookup. */
export interface LookupOption {
  /**
   * Identidade do item — vira o `value` do `Item` do ADS.
   *
   * Numérica na maioria dos lookups (`DropDownListViewModel.key`), textual nos
   * que devolvem `DropDownListStringKeyViewModel` (ex.: `person/shared/people-multiple`,
   * cujo `key` é um código de pessoa como `"USR260953"`). A união evita a
   * conversão para número desses casos, que produzia `NaN`.
   */
  readonly id: number | string;
  readonly label: string;
}

/** Número mínimo de caracteres para disparar a busca server-side. */
export const LOOKUP_MIN_CHARS = 3;

/**
 * Aplica debounce de 1s ao termo de busca e indica quando a query está pronta
 * (mínimo de {@link LOOKUP_MIN_CHARS} caracteres). Reseta imediatamente quando
 * o filtro é limpo. Base compartilhada dos hooks de dropdown de lookup.
 *
 * @param filter - Texto digitado no autocomplete.
 * @returns `{ debouncedFilter, isReady }`.
 */
export function useDebouncedFilter(filter: string) {
  const [debouncedFilter, setDebouncedFilter] = useState('');

  useEffect(() => {
    if (filter === '') {
      setDebouncedFilter('');
      return;
    }
    const timer = setTimeout(() => setDebouncedFilter(filter), 1000);
    return () => clearTimeout(timer);
  }, [filter]);

  return { debouncedFilter, isReady: debouncedFilter.length >= LOOKUP_MIN_CHARS } as const;
}

/**
 * Mapeia um dropdown do backend (`key`/`value`) para {@link LookupOption}.
 *
 * Aceita qualquer resposta estruturalmente compatível — `DropDownListViewModel`
 * e `DropDownRepositoryResponse` gerados pelo Kubb têm o mesmo formato.
 */
export function mapDropdownOptions(
  data: readonly { key?: number; value?: string | null }[] | undefined,
): readonly LookupOption[] {
  return (data ?? []).map(item => ({ id: item.key ?? 0, label: item.value ?? '' }));
}
